import React from "react";
import {isEmpty} from "lodash";
import {StringBuilder} from "../../../AppUtils.js";
import moment from "moment/moment";
import {isEqual} from "lodash";
import {openWindow} from "../../../http/WindowOpener.jsx";
import {briefDeclineOptionsToText, briefProviderDeclineOptionsToText} from "../../../event/DeclineOrderModal/consts.js";
import {Tooltip} from "antd";

export const companyBriefProjection = [
    "brifId",
    "maxPrice",
    "dtstart",
    "createdAt",
    "status",
    "description",
    "providerIds",
    "address",
    "floor",
    "participantsAmount",
    "poNumber",
    "bidsIds",
    "bids",
    "systemId",
    "watchedByAdmin",
    "assignedCSMs",
    "updatedAt",
    "adminComment",
    "order",
    "serviceId",
    "removedProviderIds",
    "briefNumber",
    "cancelReason",
    "cancelComment",
    "providerBriefState"
];

export const briefPageSize = 10;

export const createLinkElement = (href, text) => (
    <a
        href={href}
        onClick={e => {
            e.preventDefault();
            openWindow(href);
        }}
        style={{cursor: "pointer", textDecoration: "underline"}}>
        {text}
    </a>
);

export const BriefStatus = {
    pending: "pending",
    haveBids: "haveBids",
    cancelled: "cancelled",
    accepted: "accepted"
};

export const BidStatus = {
    pending: "pending",
    cancelled: "cancelled",
    accepted: "accepted",
    watched: "watched",
    haveNote: "haveNote"
};

export const BidStatusToColorCompany = {
    [BidStatus.pending]: [
        "ממתין לצפייה בהצעת מחיר",
        "",
        "var(--warning-transparent-background)",
        "var(--warning-lightes-background)"
    ],
    [BidStatus.cancelled]: ["בוטלה", "", "var(--grey-lightes-color)", "var(--grey-transparent-background)"],
    [BidStatus.accepted]: [
        "הצעה מאושרת",
        (bid, brief, providerId) => {
            const url = `/events/${bid?.order?.eventId}/orderV2`;
            return (
                <span>
                    {bid?.order?.providerBrand} | הזמנה מס {createLinkElement(url, bid?.order?.proposalId)}
                </span>
            );
        },
        "var(--lightes-accept-color)",
        "var(--light-accept-color)"
    ],
    [BidStatus.watched]: ["נצפה", "", "var(--standard-primary-color)", "var(--lightes-secondary-color)"],
    [BidStatus.haveNote]: ["נשלחו תיקונים", "", "var(--standard-primary-color)", "var(--lightes-secondary-color)"],
    ["none"]: [
        (bid, brief, providerId) =>
            brief?.removedProviderIds?.includes(providerId) ? "הסיר השתתפות" : "ממתין להצעת מחיר",
        "",
        (bid, brief, providerId) =>
            brief?.removedProviderIds?.includes(providerId)
                ? "var(--grey-lightes-color)"
                : "var(--warning-transparent-background)",
        (bid, brief, providerId) =>
            brief?.removedProviderIds?.includes(providerId)
                ? "var(--grey-transparent-background)"
                : "var(--warning-lightes-background)"
    ]
};

export const BidStatusToColorAdmin = {
    [BidStatus.pending]: [
        (bid, brief, providerId) => (!bid.watchedByAdmin ? "ממתין לשליחה ללקוח" : "נשלח ללקוח"),
        "",
        (bid, brief, providerId) =>
            !bid.watchedByAdmin ? "var(--warning-transparent-background)" : "var(--standard-primary-color)",
        (bid, brief, providerId) =>
            !bid.watchedByAdmin ? "var(--warning-lightes-background)" : "var(--lightes-secondary-color)"
    ],
    [BidStatus.cancelled]: ["בוטלה", "", "var(--grey-lightes-color)", "var(--grey-transparent-background)"],
    [BidStatus.accepted]: [
        "הצעה מאושרת",
        (bid, brief, providerId) => {
            const url = `/events/${bid?.order?.eventId}/orderV2`;
            return (
                <span>
                    {bid?.order?.providerBrand} | הזמנה מס {createLinkElement(url, bid?.order?.proposalId)}
                </span>
            );
        },
        "var(--lightes-accept-color)",
        "var(--light-accept-color)"
    ],
    [BidStatus.watched]: ["נשלח ללקוח", "", "var(--standard-primary-color)", "var(--lightes-secondary-color)"],
    [BidStatus.haveNote]: [
        (bid, brief, providerId) => (!bid.watchedByAdmin ? "ממתין לשליחת תיקונים ללקוח" : "ממתין לתיקונים מהספק"),
        "",
        (bid, brief, providerId) =>
            !bid.watchedByAdmin ? "var(--warning-transparent-background)" : "var(--warning-transparent-background)",
        (bid, brief, providerId) =>
            !bid.watchedByAdmin ? "var(--warning-lightes-background)" : "var(--warning-lightes-background)"
    ],
    ["none"]: [
        (bid, brief, providerId) => {
            if (brief?.removedProviderIds?.includes(providerId)) {
                return "הסיר השתתפות";
            }
            return "ממתין להצעת מחיר";
        },
        (bid, brief, providerId) => {
            if (brief?.removedProviderIds?.includes(providerId)) {
                const providerState = brief?.providerBriefState?.[providerId];
                const declineReason = providerState?.declineReason;
                const declineComment = providerState?.declineComment;

                if (declineReason) {
                    const declineReasonText = briefProviderDeclineOptionsToText[declineReason] || declineReason;
                    if (declineComment) {
                        return (
                            <Tooltip title={declineComment} placement="bottom">
                                <span>{`(${declineReasonText})`}</span>
                            </Tooltip>
                        );
                    }
                    return `(${declineReasonText})`;
                }
            }
            return "";
        },
        (bid, brief, providerId) =>
            brief?.removedProviderIds?.includes(providerId)
                ? "var(--grey-lightes-color)"
                : "var(--warning-transparent-background)",
        (bid, brief, providerId) =>
            brief?.removedProviderIds?.includes(providerId)
                ? "var(--grey-transparent-background)"
                : "var(--warning-lightes-background)"
    ]
};

export const BriefStatusToColorAdmin = {
    [BriefStatus.pending]: [
        brief => (brief?.watchedByAdmin ? "נשלח לספקים" : "ממתין לבניית הצעת מחיר"),
        brief => `${brief?.providerIds?.length ?? 0} ספקים / ${brief?.bids?.length ?? 0} הצעות`,
        brief => (brief?.watchedByAdmin ? "var(--standard-primary-color)" : "var(--warning-transparent-background)"),
        brief => (brief?.watchedByAdmin ? "var(--lightes-secondary-color)" : "var(--warning-lightes-background)")
    ],
    [BriefStatus.cancelled]: [
        "בוטלה",
        brief => (
            <>
                {brief?.cancelReason ? (
                    <Tooltip title={brief?.cancelComment} placement="bottom">
                        {`(${briefDeclineOptionsToText?.[brief?.cancelReason] ?? brief?.cancelReason})`}
                    </Tooltip>
                ) : null}
            </>
        ),
        "var(--grey-lightes-color)",
        "var(--grey-transparent-background)"
    ],
    [BriefStatus.accepted]: [
        "הצעה מאושרת",
        brief => {
            const bid = brief?.bids?.find(bid => bid.status === BidStatus.accepted);
            const url = `/events/${bid?.order?.eventId}/orderV2`;
            return (
                <span>
                    {bid?.order?.providerBrand} | הזמנה מס {createLinkElement(url, bid?.order?.proposalId)}
                </span>
            );
        },
        "var(--lightes-accept-color)",
        "var(--light-accept-color)"
    ],
    [BriefStatus.haveBids]: [
        brief =>
            brief?.bids?.some(bid => !bid.watchedByAdmin)
                ? new StringBuilder(`${brief?.bids?.filter(bid => !bid.watchedByAdmin)?.length ?? 0}`)
                      .append("הצעות מחיר")
                      .toString()
                : "נשלח ללקוח",
        brief => `${brief?.providerIds?.length ?? 0} ספקים / ${brief?.bids?.length ?? 0} הצעות`,
        "var(--standard-primary-color)",
        "var(--lightes-secondary-color)"
    ]
};

export const BriefStatusToColorCompany = {
    [BriefStatus.pending]: [
        "ממתין להצעת מחיר",
        "ההצעה נשלחה לבתי העסק הרלוונטים",
        "var(--warning-transparent-background)",
        "var(--warning-lightes-background)"
    ],
    [BriefStatus.cancelled]: ["בוטלה", "", "var(--grey-lightes-color)", "var(--grey-transparent-background)"],
    [BriefStatus.accepted]: [
        "הצעה מאושרת",
        brief => {
            const bid = brief?.bids?.find(bid => bid.status === BidStatus.accepted);
            const url = `/events/${bid?.order?.eventId}/orderV2`;
            return (
                <span>
                    {bid?.order?.providerBrand} | הזמנה מס {createLinkElement(url, bid?.order?.proposalId)}
                </span>
            );
        },
        "var(--lightes-accept-color)",
        "var(--light-accept-color)"
    ],
    [BriefStatus.haveBids]: [
        brief =>
            !brief?.bids?.filter(o => o.watchedByAdmin && isEmpty(o.note))?.length
                ? "ממתין להצעת מחיר"
                : new StringBuilder(brief?.bids.filter(o => o.watchedByAdmin)?.length ?? 0)
                      .append("הצעות מחיר")
                      .toString(),
        brief =>
            !brief?.bids?.filter(o => o.watchedByAdmin && isEmpty(o.note))?.length
                ? "ההצעה נשלחה לבתי העסק הרלוונטים"
                : new StringBuilder(
                      `${
                          brief?.bids?.filter(
                              o => o.status !== BidStatus.watched && o.status !== BidStatus.haveNote && o.watchedByAdmin
                          )?.length ?? 0
                      }`
                  )
                      .append("הצעות שלא נקראו")
                      .toString(),
        brief =>
            !brief?.bids?.filter(o => o.watchedByAdmin && isEmpty(o.note))?.length
                ? "var(--warning-transparent-background)"
                : "var(--standard-primary-color)",
        brief =>
            !brief?.bids?.filter(o => o.watchedByAdmin && isEmpty(o.note))?.length
                ? "var(--warning-lightes-background)"
                : "var(--lightes-secondary-color)"
    ]
};

export const BriefShortcutFilterValues = {
    PENDING_FOR_CLIENT: "pendingForClient",
    PENDING_FOR_BIDS: "pendingForBids",
    TODAY_BRIEFS: "todayOrders",
    AWAITING_ADMIN_APPROVAL: "awaitingAdminApproval"
};

export const BriefShortcutToText = {
    [BriefShortcutFilterValues.PENDING_FOR_CLIENT]: "מחכה לאישור הלקוח",
    [BriefShortcutFilterValues.PENDING_FOR_BIDS]: "מחכה להצעות ספקים",
    [BriefShortcutFilterValues.AWAITING_ADMIN_APPROVAL]: "ממתין לפעולות צוות",
    [BriefShortcutFilterValues.TODAY_BRIEFS]: "בריפים של היום"
};

export const BriefsShortcuts = [
    {
        key: BriefShortcutFilterValues.PENDING_FOR_CLIENT,
        label: BriefShortcutToText[BriefShortcutFilterValues.PENDING_FOR_CLIENT],
        value: BriefShortcutFilterValues.PENDING_FOR_CLIENT,
        statuses: [BriefStatus.haveBids],
        searchByCreated: false,
        datePicked: () => [moment(), moment().add(3, "months")],
        isChecked: ({statuses}) => isEqual(statuses, [BriefStatus.haveBids])
    },
    {
        key: BriefShortcutFilterValues.PENDING_FOR_BIDS,
        label: BriefShortcutToText[BriefShortcutFilterValues.PENDING_FOR_BIDS],
        value: BriefShortcutFilterValues.PENDING_FOR_BIDS,
        statuses: [BriefStatus.pending],
        searchByCreated: false,
        datePicked: () => [moment(), moment().add(3, "months")],
        isChecked: ({statuses}) => isEqual(statuses, [BriefStatus.pending])
    },
    {
        key: BriefShortcutFilterValues.TODAY_BRIEFS,
        label: BriefShortcutToText[BriefShortcutFilterValues.TODAY_BRIEFS],
        value: BriefShortcutFilterValues.TODAY_BRIEFS,
        searchByCreated: true,
        datePicked: () => [moment().startOf("day"), moment().endOf("day")],
        isChecked: ({searchDate, searchByCreated}) =>
            searchByCreated === true &&
            searchDate.length > 1 &&
            moment()?.diff(searchDate?.[0], "days") === 0 &&
            moment()?.diff(searchDate?.[1], "days") === 0
    },
    {
        key: BriefShortcutFilterValues.AWAITING_ADMIN_APPROVAL,
        label: BriefShortcutToText[BriefShortcutFilterValues.AWAITING_ADMIN_APPROVAL],
        value: BriefShortcutFilterValues.AWAITING_ADMIN_APPROVAL,
        searchByCreated: false,
        datePicked: () => [moment().startOf("month"), moment().add(3, "months")],
        isChecked: ({selectedOptionShortcut}) =>
            selectedOptionShortcut === BriefShortcutFilterValues.AWAITING_ADMIN_APPROVAL
    }
];

export const companySelectedActions = {
    viewBrief: "viewBrief",
    canceledBrief: "canceledBrief",
    openOffers: "openOffers",
    editBrief: "editBrief"
};

export const tableColumns = {
    status: "status",
    companyName: "companyName",
    comment: "comment",
    systemId: "systemId",
    assignedCSMs: "assignedCSMs",
    description: "description"
};
